// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { BootstrapLink } from '../../dsl/front';
import { AbstractModuleUi } from '../_abstract/ui';
import { AppTheme } from '../_contracts';
import { LineHeader, TextList, ThumbnailsList } from '../_components/ui';
import { ArticlesListVersion } from './ArticlesList.contracts';
import { getArticlesCount } from './ArticlesList.helpers';
/**
 * Component capable of rendering the `ArticlesListModule's` contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
let ArticlesListModuleUi = class ArticlesListModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        this.AppTheme = AppTheme;
        /**
         * Articles to render.
         */
        this.articles = [];
        this.headerLink = null;
        /**
         * "Read more" link.
         */
        this.readMoreLink = null;
    }
    /**
     * @inheritDoc
     */
    get articlesCount() {
        return getArticlesCount(this.module.size, this.module.version);
    }
    /**
     * Appropriate Vue component that should be used to render the articles.
     */
    get component() {
        switch (this.module.version) {
            case ArticlesListVersion.Thumbnails: {
                return ThumbnailsList;
            }
            case ArticlesListVersion.Text:
            default: {
                return TextList;
            }
        }
    }
    /**
     * Determines whether there's a header text to render.
     */
    get hasHeaderText() {
        return typeof this.module.content.header !== 'undefined' &&
            typeof this.module.content.header.text === 'string' &&
            this.module.content.header.text.length > 0;
    }
    /**
     * Shortcut for `this.module.content.header`.
     */
    get header() {
        return this.module.content.header;
    }
    /**
     * Return listConfig.
     */
    get listConfig() {
        return {
            titleVersion: this.module.content.titleVersion,
            showArticleTags: this.module.content.showArticleTags
        };
    }
    /**
     * Determines whether there's a header to be rendered.
     */
    get shouldShowHeader() {
        const { header = {} } = this.module.content;
        if (!this.module.content.showHeader) {
            return false;
        }
        const keys = ['site', 'text'];
        for (const key of keys) {
            const value = header[key];
            if (typeof value === 'string' && value.length > 0) {
                return true;
            }
        }
        return false;
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        const { list, overrides, readMoreLink, allowDuplicates, startFromBeginning, headerLink } = this.module.content;
        // Determine if there's a list to resolve.
        if (list === null)
            return;
        try {
            const articles = await this.relatedService
                .resolve(list, {
                overrides,
                take: this.articlesCount,
                preventDuplicates: !allowDuplicates,
                continueOrder: !startFromBeginning
            });
            this.articles = (Array.isArray(articles) ? articles : [articles]);
            this.onReady();
        }
        catch (e) {
            this.onError(e);
        }
        if (typeof readMoreLink !== 'undefined') {
            try {
                const content = await this.relatedService.resolve(readMoreLink);
                this.readMoreLink = { target: { path: content.urlPath } };
            }
            catch (e) {
                if (this.debug) {
                    console.warn(e);
                }
            }
        }
        if (typeof headerLink !== 'undefined' && headerLink !== null) {
            try {
                const link = await this.relatedService.resolve(headerLink);
                this.headerLink = link.urlPath;
            }
            catch (e) {
                if (this.debug) {
                    console.warn(e);
                }
            }
        }
        this.articles.length > 0 ? this.onReady() : this.onError();
    }
    /**
     * Resolves length of articles' list displayed by the module.
     */
    static resolveArticlesCount(module, list) {
        if (!module.content.list || module.content.list.value.toString() !== list.value.toString()) {
            return 0;
        }
        return getArticlesCount(module.size, module.version);
    }
};
ArticlesListModuleUi = __decorate([
    Component({
        name: 'ArticlesListModuleUi',
        components: { LineHeader, BootstrapLink },
        async created() {
            await this.fetchRelated();
        },
        template: `
    <section class="articles-list module" :class="[debugClass, loadingClass, moduleColoredClass,getThemeByBgClass]"
             :style="{ backgroundColor: customColor }">

    <!-- Header -->
    <LineHeader v-if="shouldShowHeader" :site="header.site" class="articles-list__header"
                :isBold="header.isLink || header.bold || false">
      <!-- Text -->
      <template v-if="theme !== AppTheme.PremiumZone && hasHeaderText && !headerLink">
        {{ module.content.header.text }}
      </template>

      <template v-if="theme !== AppTheme.PremiumZone && hasHeaderText && headerLink">
        <BootstrapLink :link="{target: { path: headerLink }}" style="text-decoration: none">
          {{ module.content.header.text }}
        </BootstrapLink>
      </template>
    </LineHeader>

    <!-- Articles list -->
    <component :is="component"
               v-bind="{
                   articles,
                   fallbackImage,
                   readMoreLink,
                   listConfig,
                   relatedService,
                   windowService,
                   isLazy: module.isLazyLoaded
                 }"/>

    </section>
  `
    })
], ArticlesListModuleUi);
export { ArticlesListModuleUi };
